import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';
type NavLayout = 'top' | 'bottom';

interface AppSettings {
    theme: Theme;
    desktopNavLayout: NavLayout;
    mobileNavLayout: NavLayout;
}

interface SettingsContextType {
    settings: AppSettings;
    setSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
}

const defaultSettings: AppSettings = {
    theme: 'light',
    desktopNavLayout: 'top',
    mobileNavLayout: 'bottom',
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const getStoredSettings = (): AppSettings => {
    try {
        const stored = localStorage.getItem('app-settings');
        if (stored) {
            return { ...defaultSettings, ...JSON.parse(stored) };
        }
    } catch (error) {
        console.error("Failed to parse settings from localStorage", error);
    }
    return defaultSettings;
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<AppSettings>(getStoredSettings);

    useEffect(() => {
        // Apply theme class to root element
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(settings.theme);

        // Persist settings to localStorage
        try {
            localStorage.setItem('app-settings', JSON.stringify(settings));
        } catch (error) {
            console.error("Failed to save settings to localStorage", error);
        }
    }, [settings]);

    const setSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
        setSettings(prevSettings => ({
            ...prevSettings,
            [key]: value
        }));
    };

    return (
        <SettingsContext.Provider value={{ settings, setSetting }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = (): SettingsContextType => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
